import axios from 'axios';
import config from '@/common/config';
import utils from '@/common/utils';
import { commitMutation, dispatchAction } from '@/store/storeUtils';
import store from '@/store/index';

const DEBUG = false;
const workerCallbackRegistry = {};

// Worker is initialized only in case NODE_ENV is not 'test'. This is the case when running tests.
// It is not possible to instantiate the Worker in a test environment.
export const worker = process.env.NODE_ENV !== 'test' ? new Worker('/js/web-worker.js') : () => {};

export function registerWorkerCallback(type, callback) {
  workerCallbackRegistry[type] = callback;
}

const onWorkerMessage = (event) => {
  if (event && event.data) {
    console.log('cachingUtil got message from worker', event.data.type);
    if (workerCallbackRegistry[event.data.type]) {
      workerCallbackRegistry[event.data.type](event);
    } else {
      console.log('Worker has sent message of type', event.data.type, ' But no handler is defined');
    }
  } else {
    console.log('onWorkerMessage but event is undefined');
  }
};

worker.onmessage = onWorkerMessage;
worker.onerror = (err) => {
  console.log('web worker raised error', err);
};

registerWorkerCallback('commit/sync/setCurrentSync', (event) => {
  commitMutation('sync/setCurrentSync', event.data.msg, { root: true });
});

registerWorkerCallback('dispatch/updateModuleApplicationCache', (event) => {
  console.log(`dispatch ${event.data.moduleId}/updateModuleApplicationCache`);
  dispatchAction(`${event.data.moduleId}/updateModuleApplicationCache`);
});

//Add callback for service worker to perform actions on receiving a push message
registerWorkerCallback('onWebPushReceived', (event) => {
  if (event.data) {
    /**
     *  cockpitNotificationReceived: messagingSync,
     *                             , body, app_payload,
     *                             type: 'onWebPushReceived'
     */
    const { cockpitNotificationReceived, title, body, app_payload, type } = event.data;

    console.log(
      'Received webPush',
      JSON.stringify(
        {
          cockpitNotificationReceived,
          title,
          body,
          app_payload,
          type,
        },
        null,
        2
      )
    );
    if (cockpitNotificationReceived) {
      console.log('Trigger messaging sync');
      // store.commit('messaging/markMessageAsUnreadInNextSync', {uuid: app_payload.messageUuid}, {root: true})
      store.dispatch('sync/invalidateModuleCacheAndSync', { moduleId: 'messaging' }, { root: true });
    }
    // //Navigate to chat on notification click - /messaging?id=143&isGroup=1&uuid=xyz
    // if (window.location.pathname === '/messaging') {
    //   //Avoid duplicate navigation error if user is already in messaging view
    //   router.push({ query });
    // } else {
    //   router.push({ path: '/messaging', query });
    // }
  }
});

registerWorkerCallback('pushReceived', (event) => {
  console.log('Received pushReceived event in cachingUtil', event);
  navigator.setAppBadge(150);
  // if (!event.data) return;

  // let { title, body, app_payload } = event.data;
  // let { isGroup, id } = app_payload.notificationapi;
  // if (!store.state.messaging.hasJoinedSocketRo‡om) {
  //   //Only play sound if not connected via socket because it would play twice
  //   const audio = new Audio('/assets/audio/notification-chat.ogg');
  //   audio.play(); //TODO: doesn't work if user hasn't interacted with DOM - does it work if in background?
  // }
  // const openChat = store.state.messaging.openChat;
  // if (
  //   !store.state.messaging.hasJoinedSocketRoom ||
  //   //Check if chat of received push message is currently open
  //   !openChat ||
  //   !((!isGroup && !openChat.isGroup && openChat.counterpart.id === id) || (isGroup && openChat.isGroup && openChat.id === id))
  // ) {
  //   //Show notification only if user is not in current chat
  //   navigator.serviceWorker.getRegistration().then((reg) => {
  //     const options = {
  //       data: event.data,
  //       tag: `tag_${new Date().getUTCSeconds()}`, //defineUniqueTag, may be with timestamp on second level',
  //       body: body,
  //       icon: '/img/icons/android-chrome-192x192.png', //icons are generated on build process therefore they work only as PWA
  //     };
  //     reg.showNotification(title, options);
  //   });
  // }
});
if (navigator && navigator.serviceWorker) {
  navigator.serviceWorker.onerror = (err) => {
    console.log('service worker raised error', err);
  };
  navigator.serviceWorker.onmessage = onWorkerMessage;
}

//}
/**
 * Updates the application cache of the given module.
 * The provided moduleData object needs to strictly equal the data (structure) received by the module service.
 * This cache needs to be updated when parts of this data has been modified. When being offline the app
 * service worker returns exactly this cached data.
 *
 * @param moduleData Object of data that it is received from server.
 * @param moduleId The module id used as vuex store module
 */
export async function updateModuleCache(moduleId, moduleData) {
  // avoid to check caches in non-pwa mode because even accessing caches variable throws an exception
  // if (process.env.VUE_APP_PWA_ENABLED === 'false') return;
  // Check if moduleData is accidentially empty. Do not overwrite cache in this case!!!
  let nonEmptyKeys = Object.keys(moduleData).filter((i) => Array.isArray(moduleData[i]) && moduleData[i].length > 0);
  if (nonEmptyKeys.length === 0) {
    console.log('module data is empty, do not override module cache in updateModuleCache', JSON.stringify(moduleData, null, 2));
    return;
  }
  try {
    if (location.protocol.indexOf('https') >= 0 && caches) {
      //console.log(`updateModuleCache for ${moduleId}`, moduleData);
      const stringifiedModuleData = new Response(JSON.stringify(moduleData), {
        headers: { 'Response-Type': 'basic', 'content-type': 'application/json' },
      });
      const cache = await caches.open('oevpad-api');

      await cache.put(`/api/modules/${moduleId}/`, stringifiedModuleData);
      console.log(`Update cache /api/modules/${moduleId}`);
    } else {
      console.log(`updateModuleCache for ${moduleId} not possible because not in PWA mode`);
    }
  } catch (e) {
    console.warn('updateModuleCache failed. Ignored exception is:', e);
  }
}

/**
 * Updates the localStorage for the given module with the current state of the file cache.
 *
 * @param moduleData Object of data as it is received from server.
 */
export function updateLocalStorageModuleFileState(moduleId, { files }) {
  let filesByFilenameAndChecksum = {};

  files.forEach((f) => {
    if (f._offline === true) {
      filesByFilenameAndChecksum[`${f.filename}_${f.checksum}`] = { _offline: true, _fetchTime: f._fetchTime };
    }
  });
  localStorage.setItem(`api/modules/${moduleId}`, JSON.stringify(filesByFilenameAndChecksum, null, 2));
}

/**
 * Load all files of a given module to allow service worker to cache them.
 * @param {files} files array. Each item must have an id, a checksum and a filename
 * @param {module} module, e.g document (named as the upload folder in oev-pad cockpit)
 * @param {commit} vuex commit function
 * @param {function} updateModuleCacheCallback callback to call while sync process takes place. Should be used to update
 *                                             the localStorage file offline state. As we would loose the file state in the case
 *                                             of an interrupt
 */
export async function cacheAllModuleFiles({ files, filenameAttribute = 'filename', moduleIndex, commit, dispatch }) {
  console.log('cacheAllModuleFiles', moduleIndex.moduleType);
  let moduleId = moduleIndex.moduleId;

  let listOfFilesToDownload = await getDownloadFilesAndCleanModuleCache({
    files,
    moduleIndex,
    filenameAttribute,
    moduleId,
  });

  return new Promise((resolve, reject) => {
    const strategy = 'webWorker'; //mainThread or webWorker

    if (strategy === 'mainThread') {
      downloadFiles(listOfFilesToDownload, moduleIndex, 'filename', moduleId, commit, dispatch)
        .then(async (cachedCount) => {
          return resolve();
        })
        .catch((e) => {
          console.warn('DownloadFiles failed. Ignoring', e);
          return resolve();
        });
    } else if (strategy === 'webWorker') {
      console.time(`web-worker downloadFiles - ${moduleId}`);
      registerWorkerCallback('onFilesDownloaded', async (event) => {
        // We need to take this fresh files copy from the web worker because it has been cloned when passing to the web worker and therefore the original array has not been updated.
        let { files, moduleId, cachedCount } = event.data;
        console.timeEnd(`web-worker downloadFiles - ${moduleId}`);
        console.log('web-worker cached', cachedCount, 'Files');

        // Store file cache state
        await dispatch(`${moduleId}/updateModuleApplicationCache`, {}, { root: true });

        // await clearDeprecatedModuleFiles(moduleId, files);
        resolve(); // return value not used
      });

      // Now we need to call the web worker by the means of a postMesasage. Serialization (stringify) needed here
      let payload = JSON.stringify({
        type: 'downloadFiles',
        apiUrl: config.apiUrl,
        files: listOfFilesToDownload,
        moduleIndex,
        filenameAttribute,
        moduleId,
      });
      worker.postMessage({
        type: 'downloadFiles',
        payload,
      });
    }
    // }
  });
}

const CACHED_BEFORE_THIS_SYNC = 'CACHED';
const REQUIRED_AND_CACHED = 'OK';
const DOWNLOAD_REQUIRED = 'DOWNLOAD';

/**
 * Get the list of files to download and clean the module cache at the same time from obsolete files (that is not listed anymore)
 * @param {Array} files - Array of files with at least a checksum and an attribute for the filename (described by the parameter filenameAttribute)
 * @param {Object} moduleIndex - Each module must have an index file with amongst others the api attribute. This will rule over the cache name to use
 * @param {String} filenameAttribute - Name of the attribute containing the filename, defaults to filename
 */
async function getDownloadFilesAndCleanModuleCache({ files, moduleIndex, filenameAttribute }) {
  // clear unused cached files
  try {
    let cache = await caches.open(moduleIndex.api);
    let cachedRequests = await cache.keys();
    let cachedFilesByUrl = {};
    let moduleId = moduleIndex.moduleId;

    // 1. Create map from existing
    cachedRequests.forEach((request) => {
      let url = request.url;
      cachedFilesByUrl[url] = { url, state: CACHED_BEFORE_THIS_SYNC };
      if (url.includes('?')) {
        console.log(
          'Cached file will be deleted because it was loaded with query params. This behavior has changed in 2022-09. Query params MAY NOT be used. Delete and download without query params',
          url
        );
        cache.delete(url);
      }
    });

    // 2. Add url's from given list of files
    files.forEach((file) => {
      let url = utils.constructFileUrl(moduleIndex.api, file, filenameAttribute, file.requestSuffix);
      let cached = cachedFilesByUrl[url];
      if (cached && cached.state === CACHED_BEFORE_THIS_SYNC) {
        // if (['videos', 'unfalldesk'].indexOf(moduleId) >= 0) console.log('change state for url', url, 'from ', cached.state, 'to existing');
        cached.state = REQUIRED_AND_CACHED;
      } else {
        // if (['videos', 'unfalldesk'].indexOf(moduleId) >= 0) console.log('add url', url, 'for download');

        // let key = `${file.filename}/${file.checksum}`;
        file.url = url;
        file.filename = file[filenameAttribute];
        file.state = DOWNLOAD_REQUIRED;
        cachedFilesByUrl[url] = file;
      }
    });

    // 3. Delete obsolete files
    let downloadFiles = [];
    let deleteableCount = 0;
    let downloadCount = 0;
    let urls = Object.keys(cachedFilesByUrl);
    for (let i = 0; i < urls.length; i++) {
      let url = urls[i];
      let item = cachedFilesByUrl[url];
      if (item && item.state === CACHED_BEFORE_THIS_SYNC) {
        console.log(`Delete ${moduleId}/${url}`);
        deleteableCount++;
        await cache.delete(url);
        if (['videos', 'unfalldesk'].indexOf(moduleId) >= 0) console.log('delete resource', url, 'because it got deprecated');
      } else if (item && item.state === DOWNLOAD_REQUIRED) {
        downloadFiles.push(item);
        downloadCount++;
      }
    }
    console.log(`Module files stats of ${moduleId}: Deleted:${deleteableCount}, download:${downloadCount}`);
    // if (['galleries','gallery','videos', 'unfalldesk'].indexOf(moduleId) >= 0)
    // {
    //   console.log(moduleId,'cachedFilesByUrl', JSON.stringify(cachedFilesByUrl, null, 2));
    //   console.log(moduleId,'downloadFiles', JSON.stringify(downloadFiles, null, 2));
    // }

    return downloadFiles;
  } catch (err) {
    console.warn('failed to clear deprecated module files from cache');
    //ignore
  }
  return [];
}

/**
 * Downloads all files provided and makes sure they are stored within the application cache
 *
 * @param {*} files
 * @param {*} moduleIndex
 * @param {*} filenameAttribute
 * @param {*} moduleId
 * @param {*} commit
 * @param {*} dispatch
 * @returns
 */
async function downloadFiles(files, moduleIndex, filenameAttribute, moduleId, commit, dispatch) {
  let promises = [];
  let batchNum = 0;
  let cached = 0;
  const BATCH_SIZE = moduleId === 'videos' ? 2 : 15; //total sync duration on mac marcel/lvb: batch size=10 => 18s, batch size=20 => 16s, batch size=40 => 17s

  for (let index = 0; index < files.length; index++) {
    // console.log(`Iteration ${index} in <${moduleId}> downloadFiles`);
    let file = files[index];
    let requestSuffix = file.requestSuffix;

    // Load file first from cache when not available, load it from server (handled by service worker)
    promises.push(fetchFile(moduleIndex, file, filenameAttribute, requestSuffix));

    // Start x=BATCH_SIZE requests at once and await their result
    // Using this kind of a reduced parallelism is about twice as fast compared to a serialized version
    if (promises.length >= BATCH_SIZE || index >= files.length - 1) {
      let responses;
      try {
        responses = await Promise.all(promises);
        cached += promises.length;
        console.log(`- Performed ${responses.length} caching <${moduleId}> requests in parallel (batch # ${batchNum})`);

        let msg = `${moduleId.toUpperCase().substr(0, 1)}${moduleId.substr(1)}<br/>${index} / ${files.length}`;

        commit('sync/setCurrentSync', msg, { root: true });

        promises = [];
      } catch (reason) {
        console.warn(`Failed caching files in parallel (batch ${batchNum}. Reason ${reason}`);
        console.log(JSON.stringify(reason, null, 2));
      }

      // Update application cache every 4 batches to remember caching state in case of app restart
      if (batchNum % 4 === 0) {
        dispatch(`${moduleId}/updateModuleApplicationCache`, {}, { root: true });
      }
      batchNum++;
    }
  }

  return cached;
}

/**
 * Fetch media files (pdf, video, image,...) for caching (but returns a promise only)
 * @param {*} moduleIndex The modules overview specified in module index.js file. Must contain the api property
 * @param {*} filesById
 * @param {*} file
 */
function fetchFile(moduleIndex, file, filenameAttribute = 'filename', requestSuffix = '') {
  let url = utils.constructFileUrl(moduleIndex.api, file, filenameAttribute, requestSuffix);
  return new Promise((resolve, reject) => {
    if (file._offline === true) {
      return resolve({ success: true, msg: 'already cached', url });
    }
    axios
      .get(url, { timeout: 0 }) // no timeout
      .then((response) => {
        file._offline = true;
        file._fetchTime = Date.now();
        resolve({ success: true, url });
      })
      .catch((err) => {
        // ignore exceptions here but mark file as ._offline=false
        file._offline = 500; //false; //'failed';
        file._fetchTime = Date.now();
        // console.log('Failed to cache file with id:', file.id, file.title);
        // We resolve here to avoid that the Promis.all(...) rejects because of only one failed fetch process
        resolve({ success: false, url });
      });
  });
}
